import {
  CanonicalDataType,
  isMessagePathAttr,
  MessagePathMetadataWellKnown,
  MessagePathNode,
} from "@/shared/domain/topics";
import { type LogPanelState } from "@/shared/state/visualization";

import { type ColumnDefinition } from "./columnFilters/columnDefinition";
import { columnIdForParts } from "./columnId";

export function getDynamicColumns(
  data: LogPanelState["data"],
): ColumnDefinition[] {
  // Key by the lossless column id, not the display name: the same signal across
  // files collapses into one column while distinct paths that merely share a
  // display name stay apart. The first occurrence fixes display metadata; later
  // same-id paths only append their message-path id.
  const byColumnId = new Map<string, ColumnDefinition>();
  for (const topic of data) {
    const parts = topic.messagePath.parts;
    const columnId = columnIdForParts(parts);
    const existing = byColumnId.get(columnId);
    if (existing) {
      existing.messagePathIds!.push(topic.messagePath.id);
      continue;
    }

    const finalPart = parts.at(-1);
    const isAttr = isMessagePathAttr(finalPart);
    const dataType = isAttr ? finalPart.dataType : undefined;
    const isCategorical = dataType === CanonicalDataType.Categorical;

    const rawCategories = isCategorical
      ? topic.messagePath.metadata[MessagePathMetadataWellKnown.Categories]
      : undefined;

    const categories =
      Array.isArray(rawCategories) &&
      rawCategories.length > 0 &&
      rawCategories.every((c) => typeof c === "string")
        ? rawCategories
        : undefined;

    byColumnId.set(columnId, {
      columnId,
      name: MessagePathNode.partsToString(parts),
      messagePathIds: [topic.messagePath.id],
      dataType,
      categories,
    });
  }
  return Array.from(byColumnId.values());
}

function categoriesEqual(a?: string[], b?: string[]): boolean {
  if (a === undefined && b === undefined) {
    return true;
  }
  if (a === undefined || b === undefined) {
    return false;
  }
  return a.length === b.length && a.every((value, i) => value === b[i]);
}

/**
 * Returns the existing column `candidate` would collide with, or undefined if
 * the add is safe.
 *
 * Same-named paths merge into one column, but must not when their `dataType` or
 * `categories` differ: the survivor's formatting (e.g. categorical labels) would
 * mislabel the other file's values. Returns undefined for a new name or a
 * matching-semantics merge.
 */
export function findConflictingLogColumn(
  candidate: LogPanelState["data"][number],
  existing: LogPanelState["data"],
): ColumnDefinition | undefined {
  const [candidateColumn] = getDynamicColumns([candidate]);
  if (candidateColumn === undefined) {
    return undefined;
  }
  for (const column of getDynamicColumns(existing)) {
    if (column.columnId !== candidateColumn.columnId) {
      continue;
    }
    const compatible =
      column.dataType === candidateColumn.dataType &&
      categoriesEqual(column.categories, candidateColumn.categories);
    return compatible ? undefined : column;
  }
  return undefined;
}

/**
 * User-facing reason for a blocked add. Shared by every add path (settings form,
 * drag-and-drop) so the wording stays consistent.
 */
export function logColumnConflictMessage(columnName: string): string {
  return `A "${columnName}" column with a different data type or categories is already shown. Remove it before adding this signal.`;
}
